import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {UseQueryOptions, useQuery} from "@tanstack/react-query";
import {Response} from "@QUERIES/types";

interface CouponByEvent {
	couponNo: string;
	couponNm: string;
	discDiviCd: string;
	discRate: number;
	discAmt: number;
	validityDday: string;
	validityDiviCd: string;
	validityFrDt: string;
	validityToDt: string;
}

interface CouponByUser {
	multiUseYn: string;
	couponNm: string;
	issueYn: string;
	couponNo: string;
	discDiviCd: string;
	discRate: number;
	discAmt: number;
	goodsPriceCd: string;
	goodsPriceFr: number;
	downYn: string;
	regDt: string;
	issueDt: string;
	validityFrDt: string;
	validityToDt: string;
	evAdtPrice: number;
	evChdPrice: number;
	evInfPrice: number;
	issueCnt: number;
	issueCntTot: number;
	couponDivi: string;
	custGradeCd: string;
	couponIssueSeq: string;
	discAmtNo?: number;
	applied?: string;
}

/** ```
 * 쿠폰 리스트 조회
 *   `/prdt/api/coupon/by-event/${evCd}/${webCustNo}`
 * parameter
 *   `evCd` 예약자 일련번호
 * ```*/
const key = `useCouponByEvent`;
const getPath = (evCd: string, webCustNo?: string): string | null => {
	if (!evCd) return null;
	if (webCustNo) return `${publicRuntimeConfig.ENV_PRODUCT_API_BASEPATH}/api/coupon/by-event/${evCd}/${webCustNo}`;
	return `${publicRuntimeConfig.ENV_PRODUCT_API_BASEPATH}/api/coupon/by-event/${evCd}`;
};

/** ```
 * 쿠폰 리스트 조회 by event
 *   `/prdt/api/coupon/by-event/${evCd}`
 * parameter
 *   `evCd` 예약자 일련번호
 * ```*/
const useCouponByEvent = (evCd: string, options?: Partial<UseQueryOptions<Response<CouponByEvent[]>, Error>>) =>
	useQuery({
		queryKey: [key, getPath(evCd)],
		queryFn: () => useConfigureAxios({url: getPath(evCd)}).then(r => r.data),
		...options,
	});

/** ```
 * 쿠폰 리스트 조회 by user
 *   `/prdt/api/coupon/by-event/${evCd}/${webCustNo}`
 * parameter
 *   `evCd` 예약자 일련번호
 *   `webCustNo` 회원 일련번호
 * ```*/
const useCouponByUser = (
	evCd: string,
	webCustNo: string,
	options?: Partial<UseQueryOptions<Response<CouponByUser[]>, Error>>,
) =>
	useQuery({
		queryKey: [key, getPath(evCd, webCustNo)],
		queryFn: () => useConfigureAxios({url: getPath(evCd, webCustNo)}).then(r => r.data),
		...options,
	});

export default useCouponByEvent;
export {getPath, key, useCouponByUser};
export type {CouponByEvent, CouponByUser};
